import React, { useEffect, useMemo, useRef, useState } from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import ProgressiveHeroImage from "../ProgressiveHeroImage";
import {
  SlotRenderer,
  EmptyMediaSlotAffordance,
  SlotCapacityAffordance,
  EditableSlotMediaOverlay,
  getHeroImageStyle,
  useHeroAnimation,
  useHeroAmbient,
} from "./heroModeUtils";
import "./heroModeAnimations.css";
import "./heroGalleryStrip.css";

/**
 * HeroGalleryStrip — animation-first mode where photos scroll
 * horizontally across the *top* of the hero in a continuous marquee
 * and the title / body / CTA stack *beneath* the strip.
 *
 * Sibling of ``gallery_animated``: same continuous-marquee identity,
 * but rotated 90° (X-axis scroll instead of Y) AND the text moves
 * from "alongside the strip" to "below the strip". The visual
 * identity is the horizontal flow + text-below stack; we never
 * collapse to side-by-side regardless of viewport.
 *
 * Why this exists alongside Photo Banner:
 *   * Photo Banner = ~4 fixed frames with per-photo Ken-Burns pan.
 *     Each photo is a still "establishing shot"; the motion is
 *     subtle pan inside each frame.
 *   * Gallery Strip = continuous marquee of N cards scrolling past.
 *     The photos themselves are the motion; no in-frame pan, just
 *     translateX across a long row.
 * Different rhythm, different content vibe (Photo Banner: hero
 * shots; Gallery Strip: portfolio reel).
 *
 * Marquee mechanics mirror HeroGallery (vertical sibling):
 *   1. Cards are duplicated at render time so the seam at
 *      ``translateX(-50%)`` lands on an identical copy.
 *   2. Below ``MIN_ITEMS_FOR_MARQUEE`` we skip the duplication and
 *      drop the marquee class — the loop would feel stuttery with
 *      a tiny list, so we render a static row instead.
 *   3. ``--hero-marquee-duration`` is set per-breakpoint so the
 *      on-screen card velocity stays roughly constant across
 *      viewports (smaller cards = faster loop = same perceived
 *      pixels/second).
 *
 * Reduced-motion users: ``useHeroAmbient`` returns "" and we render
 * the static row form. The shared
 * ``[class*="hero-ambient-"]`` reset in ``heroModeAnimations.css``
 * also neutralises the keyframe at the CSS level as a defence in
 * depth.
 */

// ---------------------------------------------------------------------------
// Breakpoint config
// ---------------------------------------------------------------------------
//
// The strip shrinks on smaller viewports, but the layout never
// changes orientation: photos are always on top, text always below.
// ``trackDuration`` is intentionally longer at larger breakpoints —
// more cards visible at once means each one needs more airtime to
// register; if we kept the duration constant the cards would whip past
// on big screens and drag on small ones.
//
// Sizing model — pixel-derived, *not* ``vh``-based
// -----------------------------------------------
// We used to give the strip viewport a viewport-relative height
// (``stripVh: "45vh"`` …) which scaled to the *window*, while the
// cards inside stayed at fixed pixel dimensions. On a tall window
// this produced ~80–100 px of dead vertical "slack" inside the strip
// (the track centres cards vertically, so any leftover space showed
// up as empty bands above/below every card). Stacked on top of
// ``contentPy`` and the strip's ``gap`` padding, the user saw a
// visible empty band between the photo row and the title — the
// "too much vertical empty space" symptom.
//
// The strip is now sized as ``cardH + 2 × gap``: the viewport height
// is exactly the card height plus the top/bottom gap padding that
// surrounds it. No more vh slack, regardless of window size.
//
// ``contentPy`` and ``gap`` were also tightened (roughly halved) so
// the text block under the strip sits closer to it without losing
// readable breathing room. Card pixel sizes are unchanged — the
// gallery strip's visual identity is the cards themselves; only the
// chrome around them got tighter.
const BP_CONFIG = {
  extra_large: { contentPx: 64, contentPy: 20, cardW: 380, cardH: 240, gap: 16, trackDuration: 50 },
  large:       { contentPx: 56, contentPy: 18, cardW: 340, cardH: 220, gap: 14, trackDuration: 45 },
  medium:      { contentPx: 40, contentPy: 16, cardW: 280, cardH: 180, gap: 12, trackDuration: 40 },
  small:       { contentPx: 24, contentPy: 12, cardW: 200, cardH: 130, gap: 10, trackDuration: 32 },
  extra_small: { contentPx: 16, contentPy: 10, cardW: 160, cardH: 110, gap: 8,  trackDuration: 28 },
};

// The viewport clips the horizontal marquee at the strip edges. Since the
// card shadow paints outside the card box, non-flush cards need extra
// vertical breathing room or the blur gets shaved off on phone breakpoints.
const STRIP_CARD_SHADOW_BLEED_PX = 18;

// Strip viewport height = the card itself + the symmetric vertical padding
// applied on the JS side (``paddingTop / paddingBottom`` of the viewport).
// Non-flush cards include shadow bleed; flush cards intentionally remain
// edge-to-edge because their chrome removes the shadow entirely.
function getStripHeightPx(config, isFlush) {
  const verticalInset = isFlush ? 0 : config.gap + STRIP_CARD_SHADOW_BLEED_PX;
  return config.cardH + 2 * verticalInset;
}

// Minimum logical items before we duplicate for the seamless loop.
// Below this, the marquee is mostly empty space scrolling past one or
// two tiny tiles — the loop reads as a glitch instead of a flow. We
// fall back to a plain static row in that case (same threshold
// HeroGallery uses).
const MIN_ITEMS_FOR_MARQUEE = 3;

// ---------------------------------------------------------------------------
// HeroGalleryStrip main renderer
// ---------------------------------------------------------------------------

const HeroGalleryStrip = ({
  item,
  heroBase,
  heroSlots,
  forceCenteredContent = false,
}) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  // Flush variant shares this renderer with ``gallery_strip`` (see
  // dispatch comment in ``HeroAutomatic``). The mode id is the only
  // place we can read which finish the user picked — slot contract
  // / locks / overrides are inherited unchanged. The flush flag
  // flips card chrome (corner radius / shadow / border) AND zeros
  // every spacing axis around the strip: inter-card gap, the
  // strip's vertical breathing-room (top/bottom padding on the
  // viewport), and the leading/trailing track padding (the
  // half-gap that normally keeps the first/last cards from
  // butting against the hero edge). All four go to zero so the
  // marquee reads as one continuous photo reel sliding past.
  const isFlush = typeof item?.hero_mode === "string"
    && item.hero_mode.endsWith("_flush");
  const gallerySlotData = heroSlots?.getSlot("gallery");
  // ``max_count > 1`` slots serialize as an array (possibly empty); the
  // single-assignment shape stays as an object. Normalise so the rest
  // of the renderer can iterate uniformly.
  const galleryItems = useMemo(() => {
    if (!gallerySlotData) return [];
    if (Array.isArray(gallerySlotData)) return gallerySlotData;
    return [gallerySlotData];
  }, [gallerySlotData]);
  const images = useMemo(
    // The strip's slot only accepts ``image`` (see
    // ``GalleryStripMode.slots`` on the backend); legacy refs of other
    // shapes are silently filtered so a malformed payload doesn't
    // explode the renderer.
    () => galleryItems.filter((it) => it?.type === "image"),
    [galleryItems],
  );
  const hasImages = images.length > 0;

  const config = BP_CONFIG[bp] || BP_CONFIG.medium;
  const stripVerticalInset = isFlush
    ? 0
    : config.gap + STRIP_CARD_SHADOW_BLEED_PX;

  const entrance = heroSlots?.animatedEntrance || "fade-up";
  // Strip's signature ambient animation is ``marquee-x``, declared by
  // ``GalleryStripMode.ambient_animation``. ``useHeroAmbient`` returns
  // "" under ``prefers-reduced-motion`` so the strip sits still for
  // those users. The marquee class is applied to the track itself,
  // not the viewport — the viewport is the clipping window.
  const ambientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const contentAnim = useHeroAnimation(isAnimated, entrance, { delay: 200 });

  // Decide whether to actually loop or render statically:
  // - reduced motion / explicit ambient-off → static
  // - too few cards to feel continuous → static
  // The duplicate-children trick below is gated on the same condition.
  const shouldMarquee = !!ambientClass && images.length >= MIN_ITEMS_FOR_MARQUEE;
  const trackGap = isFlush ? 0 : config.gap;
  // A marquee has no real "start" or "end"; padding only exists at
  // the physical ends of the moving element, so it creates a visible
  // snap when the loop jumps from the second half back to the first.
  // Keep edge padding only for the static row where the ends are real.
  const trackEdgePadding = shouldMarquee || isFlush ? 0 : config.gap;

  // ─── Infinite-loop sizing ──────────────────────────────────────────
  // The marquee keyframe translates the track by ``-50%`` of its own
  // width and snaps back. For the loop to read as truly infinite (no
  // visible "end of the gallery") the *first half* of the track must
  // already be wide enough to cover the visible viewport — otherwise
  // when the animation reaches ``-50%`` the duplicated half ends
  // before the right edge of the viewport and a strip of bare hero
  // background shows through. That is the user-reported "the gallery
  // ends and there's nothing to show" symptom.
  //
  // Strategy: measure the strip viewport width at runtime, compute how
  // many copies of ``images`` we need to make a single half wider than
  // the viewport, then render ``2 × repsPerHalf`` copies total so the
  // seam at ``-50%`` still lands on an identical copy.
  const stripViewportRef = useRef(null);
  const [stripViewportWidth, setStripViewportWidth] = useState(0);
  useEffect(() => {
    const node = stripViewportRef.current;
    if (!node) return undefined;
    const update = () => setStripViewportWidth(node.offsetWidth || 0);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  // Single-set width: N cards + (N-1) inter-card gaps. Ignores the
  // outer track padding — that's a small constant that biases the
  // estimate slightly conservative, which is the safe direction.
  const singleSetWidthPx = useMemo(() => {
    if (!images.length) return 0;
    return images.length * config.cardW + Math.max(0, images.length - 1) * trackGap;
  }, [images.length, config.cardW, trackGap]);

  // ``repsPerHalf`` is how many times we need to repeat the image set
  // *within each half of the track* so that one half ≥ viewport width.
  // We default to 1 until we've measured (initial paint), which
  // matches the legacy behaviour and avoids over-rendering on the
  // first frame for the common "few-images-on-narrow-viewport" case.
  const repsPerHalf = useMemo(() => {
    if (!shouldMarquee || singleSetWidthPx === 0 || stripViewportWidth === 0) {
      return 1;
    }
    return Math.max(1, Math.ceil(stripViewportWidth / singleSetWidthPx));
  }, [shouldMarquee, singleSetWidthPx, stripViewportWidth]);

  // Build the track. Total copies = ``2 × repsPerHalf`` (always even,
  // so the ``-50%`` seam splits the track into two visually identical
  // halves). Each card carries ``duplicate: true`` once it's in the
  // second half — the reduced-motion CSS fallback hides those so users
  // who don't see the loop don't see N×2 copies of every photo.
  const trackItems = useMemo(() => {
    if (!shouldMarquee) {
      return images.map((img, i) => ({ img, key: `g-${i}`, duplicate: false }));
    }
    const out = [];
    const totalCopies = 2 * repsPerHalf;
    for (let copy = 0; copy < totalCopies; copy++) {
      const isDuplicate = copy >= repsPerHalf;
      images.forEach((img, i) => {
        out.push({ img, key: `g-c${copy}-${i}`, duplicate: isDuplicate });
      });
    }
    return out;
  }, [images, shouldMarquee, repsPerHalf]);

  const heroBackgroundLayer = (
    <Background
      bgImage={heroBase.bgImage}
      imageStyleClasses={heroBase.imageStyleClasses}
      imageInlineStyles={heroBase.imageInlineStyles}
      colorClass={heroBase.colorClass}
      colorStyle={heroBase.colorStyle}
      jsPatternLayer={heroBase.jsPatternLayer}
    />
  );

  // Strip's natural height is ``stripVh`` (the photo row) + the text
  // column's intrinsic content. The shared ``responsive_height``
  // default sits a ``minHeight: baseHeightPx`` (~600px) on both the
  // hero root and the inner flex row, which is generally a good floor
  // for fully-decorated heroes — but here the row has a *fixed-height*
  // strip on top and a short text block beneath, so any leftover floor
  // shows up as a visible band of empty background above and below the
  // strip (the user-reported "lots of empty space" symptom).
  //
  // We relax that floor only in the *filled* branch (below) and only
  // when ``responsive_height`` is true: the hero collapses to its
  // natural strip + text height. The empty branch keeps the standard
  // floor so editors still have a comfortable canvas to work in
  // before they wire up images. Users on the explicit fixed-height
  // path (``responsive_height: false``) are also untouched — they
  // chose a height and we honour it.
  const tightHeroOverride = responsiveHeight ? { minHeight: 0 } : null;

  // Empty state: mirror the other animation-first modes. Text is
  // centred in a single readable column so the editor still has
  // something to look at while the user wires up images.
  if (!hasImages) {
    return (
      <div
        ref={heroRootRef}
        data-hero-root="true"
        className={containerClasses}
        style={{ ...containerStyle, ...fullBleedStyle }}
      >
        {heroBackgroundLayer}
        <ContainerEffectOverlay data={heroBase.data} />
        <div
          className={
            responsiveHeight
              ? "relative z-10 flex items-center justify-center w-full"
              : "absolute inset-0 z-10 flex items-center justify-center"
          }
          style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
        >
          <div
            ref={contentAnim.ref}
            className={`flex flex-col gap-4 w-full max-w-2xl mx-auto ${
              forceCenteredContent ? "hero-gallery-strip-content--center" : ""
            } ${contentAnim.className}`}
            style={{ padding: config.contentPx, ...contentAnim.style, ...contentInsetStyle }}
          >
            {/* Edit-mode "+ Add Image" affordance for the empty
                gallery slot. Hidden outside the editor. */}
            <EmptyMediaSlotAffordance
              heroSlots={heroSlots}
              heroId={item?.id}
              slotName="gallery"
              order={0}
            />
            <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} className="hero-gallery-strip-slot" />
            <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} className="hero-gallery-strip-slot" />
            <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} className="hero-gallery-strip-slot" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle, ...(tightHeroOverride || {}) }}
    >
      {heroBackgroundLayer}
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={`flex flex-col w-full ${
          responsiveHeight ? "relative z-10" : "absolute inset-0 z-10"
        }`}
        // No ``minHeight`` in the responsive-height path — the row
        // sizes to ``stripVh`` + the text column's intrinsic height,
        // matching the heroRoot override above. The fixed-height
        // path (``absolute inset-0``) doesn't need an inline
        // ``minHeight`` either: it already fills heroRoot via
        // ``inset: 0``.
      >
        {/* Photo strip — always at the top, regardless of breakpoint.
            ``flex-shrink-0`` pins the strip's height so the text
            column below absorbs all remaining vertical space; without
            this the marquee track's intrinsic width-driven sizing
            could push the text column off-screen on tall hero
            settings. The viewport height is derived from the card
            dimensions plus its vertical inset instead of a viewport
            percentage, so we never end up with a tall window
            stretching the strip and leaving vertical "slack" around
            the fixed-height cards inside it — see the BP_CONFIG
            comment above for the full rationale. */}
        <div
          ref={stripViewportRef}
          className="hero-gallery-strip-viewport flex-shrink-0"
          style={{
            height: `${getStripHeightPx(config, isFlush)}px`,
            paddingTop: stripVerticalInset,
            paddingBottom: stripVerticalInset,
          }}
        >
          <div
            className={`hero-gallery-strip-track ${
              shouldMarquee
                ? ambientClass
                : "hero-gallery-strip-track--static"
            }`}
            style={{
              gap: `${trackGap}px`,
              paddingLeft: trackEdgePadding,
              paddingRight: trackEdgePadding,
              "--hero-marquee-seam-offset": `${trackGap / 2}px`,
              "--hero-marquee-duration": `${config.trackDuration}s`,
            }}
          >
            {trackItems.map(({ img, key, duplicate }, i) => {
              // ``ImageSerializer`` exposes ``image_url`` (raw) and
              // ``cropped_image_url`` (respects the user's crop).
              // Prefer the cropped variant so the strip matches what
              // the user sees in the rest of the editor; fall back
              // to the raw URL when no crop is applied.
              const src = img.cropped_image_url || img.image_url;
              const { className: filterClass, style: filterStyle } =
                getHeroImageStyle(img);
              return (
                <div
                  key={key}
                  data-hero-strip-duplicate={duplicate ? "true" : "false"}
                  className={`relative hero-gallery-strip-card ${isFlush ? "hero-gallery-strip-card--flush" : ""}`}
                  style={{
                    width: `${config.cardW}px`,
                    height: `${config.cardH}px`,
                  }}
                >
                  {src ? (
                    <>
                      <ProgressiveHeroImage
                        src={src}
                        alt={img.alt_text || ""}
                        loading={i === 0 && !duplicate ? "eager" : "lazy"}
                        draggable={false}
                        className={filterClass}
                        style={filterStyle}
                      />
                      <EditableSlotMediaOverlay slotData={img} heroItem={item} />
                    </>
                  ) : (
                    <div className="hero-gallery-strip-card__empty" />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <SlotCapacityAffordance
          heroSlots={heroSlots}
          heroId={item?.id}
          slotName="gallery"
          currentCount={images.length}
          className="mx-4 mt-3"
        />

        {/* Text section beneath the strip. ``min-w-0`` mirrors the
            same trick HeroSplit / HeroPhotoBanner use: a wide
            ButtonCollection or long word can otherwise pin the
            column to its widest child's intrinsic width and push
            text past the visible hero. ``flex-1`` absorbs the
            remaining vertical space so the text doesn't crush
            against the strip. */}
        <div
          ref={contentAnim.ref}
          className={`flex flex-col justify-center gap-4 flex-1 min-w-0 ${
            forceCenteredContent ? "hero-gallery-strip-content--center" : ""
          } ${contentAnim.className}`}
          style={{
            paddingLeft: config.contentPx,
            paddingRight: config.contentPx,
            paddingTop: config.contentPy,
            paddingBottom: config.contentPy,
            ...contentAnim.style,
          }}
        >
          <SlotRenderer slotName="heading" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={1} className="hero-gallery-strip-slot" />
          <SlotRenderer slotName="body" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={2} className="hero-gallery-strip-slot" />
          <SlotRenderer slotName="cta" heroSlots={heroSlots} heroBase={heroBase} heroId={item?.id} slotIndex={3} className="hero-gallery-strip-slot" />
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroGalleryStrip);
